import React, { useState } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { CalendarDays, CheckCircle, Sparkles, Bell, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { statusColors } from "./ComplianceScoreRing";

export function MilestoneRow({ ms, contractMap, onUpdate, onDelete }) {
  const days = ms.due_date ? Math.ceil((new Date(ms.due_date) - new Date()) / 86400000) : null;
  const [editing, setEditing] = useState(false);
  const [editDate, setEditDate] = useState(ms.due_date || "");
  const [showReminder, setShowReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState(ms.reminder_date || "");

  const handleDateSave = async () => { await onUpdate(ms.id, { due_date: editDate }); setEditing(false); };
  const handleSetReminder = async () => {
    try {
      await apiClient.post("/compliance/set-reminder", { item_type: "milestone", item_id: ms.id, reminder_date: reminderDate });
      toast.success("Reminder set"); setShowReminder(false);
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col gap-2 p-4 bg-white dark:bg-slate-900 border rounded-sm" data-testid={`milestone-${ms.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="font-medium text-sm truncate">{ms.title}</p>
            <Badge className={`text-xs rounded-sm ${statusColors[ms.status]}`}>{ms.status.replace("_", " ")}</Badge>
            {ms.source === "ai_extracted" && <Badge className="text-xs rounded-sm bg-violet-100 text-violet-700"><Sparkles size={10} className="mr-1" /> AI</Badge>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            <span>{contractMap[ms.contract_id] || ms.contract_id?.substring(0, 8)}</span>
            {editing ? (
              <span className="flex items-center gap-1">
                <Input type="date" className="h-6 rounded-sm text-xs w-36" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs rounded-sm" onClick={handleDateSave}>Save</Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setEditing(false)}>Cancel</Button>
              </span>
            ) : (
              <span className="flex items-center gap-1 cursor-pointer hover:text-slate-700" onClick={() => setEditing(true)}>
                <CalendarDays size={12} /> {ms.due_date || "No date"}
                {days !== null && !["met", "completed"].includes(ms.status) && (
                  <span className={days < 0 ? "text-red-600 font-medium" : days <= 7 ? "text-amber-600" : "text-slate-500"}>
                    ({days < 0 ? `${Math.abs(days)}d overdue` : `${days}d left`})
                  </span>
                )}
              </span>
            )}
            {ms.reminder_date && <span className="flex items-center gap-1 text-amber-600"><Bell size={10} /> Reminder: {ms.reminder_date}</span>}
          </div>
          {ms.description && <p className="text-xs text-slate-400 mt-1 line-clamp-2">{ms.description}</p>}
        </div>
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          {["met", "completed"].includes(ms.status) && <Badge className="text-xs rounded-sm bg-emerald-100 text-emerald-700"><CheckCircle size={12} className="mr-1" /> Met</Badge>}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-500" onClick={() => setShowReminder(!showReminder)}><Bell size={14} /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => onDelete(ms.id)}><Trash2 size={14} /></Button>
        </div>
      </div>
      {showReminder && (
        <div className="flex items-center gap-2 pt-2 border-t">
          <Label className="text-xs whitespace-nowrap">Reminder Date:</Label>
          <Input type="date" className="h-8 rounded-sm text-xs w-40" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} />
          <Button size="sm" className="h-8 rounded-sm text-xs" onClick={handleSetReminder} disabled={!reminderDate}>Set</Button>
        </div>
      )}
    </div>
  );
}
